import { IonItem } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { type Project } from "@/lib/backend/api";
import { queryOptions_signed_key_project } from "@/lib/requests/file/file.get-signed-key";
import { Routes } from "@/lib/router";

type ProjectCardUpcomingProps = {
  project?: Project;
};

export function ProjectCardUpcoming({ project }: ProjectCardUpcomingProps) {
  const myQueryOptions = queryOptions_signed_key_project([project?.id ?? "", project?.image ?? ""]);
  const { data: src } = useQuery(myQueryOptions);

  const userFunctions = project?.stats?.uniqueUserFunctions?.join(", ") ?? "";

  const thisYear = new Date().getFullYear();
  const startDate = project?.startDate ? new Date(project.startDate) : undefined;
  const endDate = project?.endDate ? new Date(project.endDate) : undefined;
  const isStartAndEndDateBothThisYear =
    startDate?.getFullYear() === thisYear && endDate?.getFullYear() === thisYear;

  // Check if start and end dates are the same day
  const isSameDay =
    startDate &&
    endDate &&
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();

  if (!project) {
    return null;
  }

  return (
    <IonItem
      button
      detail={false}
      routerLink={Routes.ProjectOverview(
        { id: project.id },
        { search: { fromUrl: Routes.ProjectOverview({ id: project.id }) } },
      )}
      routerDirection="forward"
      className="ion-no-padding w-full [--border-width:0] [--inner-padding-end:0] [--padding-end:0] [--padding-start:0]"
      lines="none"
    >
      <Card className="w-full overflow-hidden border-none bg-surface-primary shadow-none">
        <div className="flex w-full">
          {/* Left side - Image (only if src exists) */}
          {src?.url && (
            <div className="relative h-28 w-24 shrink-0">
              <img src={src.url} alt={project.name} className="h-full w-full object-cover" />
            </div>
          )}

          {/* Right side - Content */}
          <CardContent className="min-w-0 flex-1 p-3 text-sm">
            <div className="flex h-full flex-col justify-between">
              <div className="min-w-0 space-y-1">
                <h3 className="truncate text-ellipsis whitespace-nowrap text-md font-bold text-txt-primary">
                  {project.name}
                </h3>
                <p className="overflow-hidden text-ellipsis whitespace-nowrap font-bold text-txt-brand">
                  {userFunctions}
                </p>
              </div>
              <div className="flex items-center justify-between font-medium text-txt-primary">
                <span className="truncate">
                  {startDate?.toLocaleDateString("nl-NL", {
                    year: isStartAndEndDateBothThisYear ? undefined : "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                  {endDate && !isSameDay && " - "}
                  {endDate &&
                    !isSameDay &&
                    endDate.toLocaleDateString("nl-NL", {
                      year: isStartAndEndDateBothThisYear ? undefined : "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                </span>
                {project.city && <span className="ml-2 truncate">{project.city}</span>}
              </div>
            </div>
          </CardContent>
        </div>
      </Card>
    </IonItem>
  );
}
